<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: NavBar                                                |
| Section: components                                              |
| Purpose: Primary site navigation with multi-level menu groups.   |
+------------------------------------------------------------------+
-->
<template>
    <transition name="nav-overlay-fade">
        <div v-if="dropdown || isFocused" class="nav-overlay"></div>
    </transition>
    <nav
        :class="['NavBar', { 'NavBar--home': isHome, 'NavBar--overview': isOverviewPage }]"
        :aria-label="$t('nav.mainNavigation')" @keydown.esc.stop="handleEscape">
        <ul class="hide-on-mobile primary-navigation">
            <li class="brand-link"><router-link :to="localePath('/')" :aria-label="$t('nav.home')"><img
                        src="@/assets/monogram.svg" alt="" /></router-link></li>
            <li v-for="item in navigationItems" :key="item.id"
                :ref="item.children ? setDropdownRef(item.id) : undefined"
                :class="{ 'NavBar__dropdown-trigger--active': dropdown === item.id }"
                @mouseenter="item.children && openDropdown(item.id)"
                @mouseleave="item.children && handleDesktopMenuLeave($event, item.id)"
                @focusout="item.children && handleDesktopFocusOut($event, item.id)">
                <router-link v-if="item.path" :to="localePath(item.path)"
                    :class="{ 'active-link': isNavigationItemActive(item) }">{{ $t(item.labelKey) }}</router-link>
                <template v-else>
                    <button type="button" class="a nav-disclosure" :class="{ 'active-link': isGroupActive(item) }"
                        :aria-expanded="dropdown === item.id" :aria-controls="`desktop-menu-${item.id}`"
                        @focus="openDropdown(item.id, $event)" @click.stop="toggleDropdown(item.id, $event)">
                        <span>{{ $t(item.labelKey) }}</span>
                        <img :src="dropdownIconSrc" alt="" />
                    </button>
                    <ul v-show="dropdown === item.id" :id="`desktop-menu-${item.id}`" class="dropdown">
                        <li v-for="child in item.children" :key="child.id">
                            <router-link :to="localePath(child.path)"
                                :class="{ 'active-link': isNavigationItemActive(child) }">
                                {{ $t(child.labelKey) }}<span v-if="child.badgeKey" class="nav-tag">{{ $t(child.badgeKey) }}</span>
                            </router-link>
                        </li>
                        <template v-if="item.id === 'tree'">
                            <li class="dropdown-divider" aria-hidden="true"></li>
                            <li class="dropdown-section-label">{{ $t('nav.ancestorLines') }}</li>
                            <li v-for="family in familyList" :key="family.name" class="ancestor-family"
                                @mouseenter="handleFamilyHover(family.name)" @mouseleave="hoveredFamily = null"
                                @focusout="handleFamilyFocusOut($event, family.name)">
                                <button type="button" class="subdropdown-link"
                                    :aria-expanded="isFamilyDropdownVisible(family.name)"
                                    :aria-controls="`desktop-line-${family.slug}`"
                                    @focus="handleFamilyHover(family.name)" @click.stop="toggleFamily(family.name)">
                                    <span>{{ family.name }}</span>
                                    <img src="@/assets/icons/dropdown.svg" alt="" />
                                </button>
                                <ul v-show="isFamilyDropdownVisible(family.name)" :id="`desktop-line-${family.slug}`"
                                    :ref="setFamilySubdropdownRef(family.name)" class="subdropdown" @click.stop
                                    :style="{ '--subdropdown-offset-y': `${getSubdropdownOffset(family.name)}px` }">
                                    <li v-for="person in family.members || []" :key="person.personID">
                                        <router-link :to="localePath(`/person/${person.personID}`)">
                                            <span>{{ person.firstname }} {{ person.lastname }}</span>
                                            <span>{{ formatYears(person.birthdatetr, person.altbirthdatetr,
                                                person.deathdatetr, person.burialdatetr, person.living, locale) }}</span>
                                        </router-link>
                                    </li>
                                </ul>
                            </li>
                        </template>
                    </ul>
                </template>
            </li>
        </ul>

        <ul class="hide-on-mobile height-fix">
            <li :class="['search-li hide-on-tablet', { 'NavBar__search--active': isFocused && !dropdown }]"
                v-if="!isSearchPage">
                <div :class="['search-box', { 'focused': isFocused }]" ref="searchBox" @click.stop>
                    <div class="search-icon">
                        <img src="@/assets/icons/magnifier.svg">
                    </div>
                    <input type="text" :placeholder="$t('nav.searchPlaceholder')" v-model="searchQuery"
                        :aria-label="$t('nav.searchPlaceholder')"
                        @focus="isFocused = true" @keyup.enter="submitSearch" @input="onSearchInput" />
                    <button type="button" :aria-label="$t('nav.search')" @click="submitSearch">
                        <img src="@/assets/icons/search_submit.svg" alt="">
                    </button>
                    <ul v-if="isFocused && (searchSuggestions.length || searchQuery.trim())" class="search-suggestions">
                        <li v-for="suggestion in searchSuggestions" :key="suggestion.personID"
                            @click="handleSuggestionClick(suggestion, $event)"
                            @auxclick="handleSuggestionAuxClick(suggestion, $event)"
                            @keydown.enter.prevent="handleSuggestionClick(suggestion, $event)"
                            @keydown.space.prevent="handleSuggestionClick(suggestion, $event)" tabindex="0" role="link"
                            :aria-label="`Ga naar persoon ${suggestion.firstname || ''} ${suggestion.lastname || ''}`">
                            <span v-if="isPrivateSuggestion(suggestion)" class="locked_name">
                                <img src="@/assets/icons/lock_black.svg"> {{ $t('nav.privacyHidden') }}
                            </span>
                            <span v-else>
                                {{ suggestion.firstname }} {{ suggestion.lastname }}
                            </span>
                            <br>
                            <span v-if="isPrivateSuggestion(suggestion)"></span>
                            <span v-else>{{ formatYears(suggestion.birthdatetr, suggestion.altbirthdatetr,
                                suggestion.deathdatetr, suggestion.burialdatetr, suggestion.living, locale) }}</span>
                        </li>
                        <hr v-if="searchSuggestions.length">
                        <li class="all-search-results">
                            <a :href="currentSearchHref" @click.prevent="submitSearch">{{ $t('nav.searchAllResults')
                                }}</a>
                        </li>
                    </ul>
                </div>
            </li>

            <li class="show-on-tablet" v-if="!isSearchPage">
                <router-link :to="localePath('/search')">
                    <img src="@/assets/icons/magnifier-white.svg" alt="icon">
                </router-link>
            </li>

            <!-- Conditionally show login or logout -->
            <li v-if="authToken" :class="{ 'NavBar__dropdown-trigger--active': dropdown === 'User' }"
                @mouseenter="openDropdown('User')" @mouseleave="handleDesktopMenuLeave($event, 'User')"
                @focusout="handleDesktopFocusOut($event, 'User')" ref="userDropdown">
                <button type="button" class="a nav-disclosure" :aria-expanded="dropdown === 'User'"
                    aria-controls="desktop-menu-user" @focus="openDropdown('User', $event)"
                    @click.stop="toggleDropdown('User', $event)">
                    <span>{{ $t('nav.loggedInAs', { username }) }}</span><img :src="dropdownIconSrc" alt="" />
                </button>
                <ul v-show="dropdown === 'User'" id="desktop-menu-user" ref="userContent" class="dropdown ">
                    <li v-if="isAdmin"><router-link :to="localePath('/beheer/activiteit')">{{
                        $t('nav.activityLog') }}</router-link></li>
                    <li><button type="button" class="dropdown-action" @click="logout">{{ $t('nav.logout') }}</button>
                    </li>
                </ul>
            </li>
            <li v-else>
                <router-link :to="localePath('/login')">{{ $t('nav.login') }}</router-link>
            </li>

            <li :class="{ 'NavBar__dropdown-trigger--active': dropdown === 'Language' }"
                @mouseenter="openDropdown('Language')" @mouseleave="handleDesktopMenuLeave($event, 'Language')"
                @focusout="handleDesktopFocusOut($event, 'Language')" ref="languageDropdown">
                <button type="button" class="a nav-disclosure" :aria-expanded="dropdown === 'Language'"
                    aria-controls="desktop-menu-language" @focus="openDropdown('Language', $event)"
                    @click.stop="toggleDropdown('Language', $event)">
                    <span>{{ locale.toUpperCase() }}</span><img :src="dropdownIconSrc" alt="" />
                </button>
                <ul v-show="dropdown === 'Language'" id="desktop-menu-language" class="dropdown dropdown--language">
                    <li><a :href="localeHref('nl')" @click.prevent="changeLocale('nl')">{{
                        $t('nav.languageOptionNl') }}</a></li>
                    <li><a :href="localeHref('en')" @click.prevent="changeLocale('en')">{{
                        $t('nav.languageOptionEn') }}</a></li>
                    <li><a :href="localeHref('id')" @click.prevent="changeLocale('id')">{{
                        $t('nav.languageOptionId') }}</a></li>
                    <li class="language-disclaimer">{{ $t('nav.translationsInProgress') }}</li>
                </ul>
            </li>
        </ul>

        <ul class="show-on-mobile">
            <li>
                <router-link :to="localePath('/')" :aria-label="$t('nav.home')"><img src="@/assets/monogram.svg"
                        alt="" /></router-link>
            </li>
        </ul>
        <ul class="show-on-mobile button-group">
            <li>
                <router-link :to="localePath('/search')" :aria-label="$t('nav.search')">
                    <img src="@/assets/icons/magnifier-white.svg" alt="">
                </router-link>
            </li>
            <li>
                <button ref="mobileMenuButton" type="button" class="mobile-icon-button" :aria-label="$t('nav.menu')"
                    :aria-expanded="isMenuOpen" aria-controls="mobile-navigation" @click="toggleMobileMenu">
                    <img src="@/assets/icons/menu-white.svg" alt="">
                </button>
            </li>
        </ul>

        <transition name="menu-slide">
            <div v-if="isMenuOpen" id="mobile-navigation" ref="mobileMenuPanel" class="mobile-menu" role="dialog"
                aria-modal="true" :aria-hidden="activeSubmenu ? 'true' : undefined" :inert="Boolean(activeSubmenu)"
                :aria-label="$t('nav.menu')" @keydown.tab="trapMobileFocus">
                <div class="mobile-menu-header">
                    <button ref="mobileCloseButton" type="button" @click="closeMobileMenu" class="close-button"
                        :aria-label="$t('nav.closeMenu')">
                        <img src="@/assets/icons/cross-white.svg" alt="" />
                    </button>
                    <h2>{{ $t('nav.menu') }}</h2>
                    <hr>
                </div>

                <ul>
                    <li v-for="item in navigationItems" :key="`mobile-${item.id}`">
                        <router-link v-if="item.path" :to="localePath(item.path)"
                            :class="{ 'active-link': isNavigationItemActive(item) }" @click="closeMobileMenu">{{
                                $t(item.labelKey) }}</router-link>
                        <button v-else type="button" class="mobile-navigation-button"
                            :class="{ 'active-link': isGroupActive(item) }" @click="openSubmenu(item.id)">
                            <span>{{ $t(item.labelKey) }}</span><img src="@/assets/icons/dropdown.svg" alt="" />
                        </button>
                    </li>
                    <li><router-link :to="localePath('/search')" @click="closeMobileMenu">{{ $t('nav.search')
                            }}</router-link></li>
                    <hr>
                    <li v-if="authToken && isAdmin"><router-link :to="localePath('/beheer/activiteit')"
                            @click="closeMobileMenu">{{ $t('nav.activityLog') }}</router-link></li>
                    <li v-if="authToken"><button type="button" class="mobile-navigation-button" @click="logout">{{
                        $t('nav.logout') }}</button></li>
                    <li v-else><router-link :to="localePath('/login')" @click="closeMobileMenu">{{ $t('nav.login')
                            }}</router-link></li>
                    <hr>
                    <li class="mobile-language-links">
                        <a :href="localeHref('nl')" @click.prevent="changeLocale('nl')"
                            :class="{ 'active-link': locale === 'nl' }">🇳🇱
                            NL</a>
                        ·
                        <a :href="localeHref('en')" @click.prevent="changeLocale('en')"
                            :class="{ 'active-link': locale === 'en' }">🇬🇧
                            EN</a>
                        ·
                        <a :href="localeHref('id')" @click.prevent="changeLocale('id')"
                            :class="{ 'active-link': locale === 'id' }">🇮🇩
                            ID</a>
                    </li>
                </ul>
            </div>
        </transition>

        <transition name="submenu-slide">
            <div v-if="activeSubmenu" ref="mobileSubmenuPanel" class="mobile-submenu" role="dialog" aria-modal="true"
                :aria-label="activeFamily || $t(activeNavigationGroup.labelKey)" @keydown.tab="trapMobileFocus">
                <div class="mobile-menu-header">
                    <button ref="mobileBackButton" type="button" @click="closeSubmenu" class="back-button"
                        :aria-label="$t('nav.back')">
                        <img src="@/assets/icons/arrow-left-white.svg" alt="" />
                    </button>
                    <h2>{{ activeFamily || $t(activeNavigationGroup.labelKey) }}</h2>
                </div>
                <ul v-if="activeFamily">
                    <li v-for="person in getFamilyMembers(activeFamily)" :key="person.personID">
                        <router-link class="person" :to="localePath(`/person/${person.personID}`)"
                            @click="closeMobileMenu">
                            <span>{{ person.firstname }} {{ person.lastname }}</span>
                            <span>{{ formatYears(person.birthdatetr, person.altbirthdatetr, person.deathdatetr,
                                person.burialdatetr, person.living, locale) }}</span>
                        </router-link>
                    </li>
                </ul>
                <ul v-else>
                    <li v-for="child in activeNavigationGroup.children" :key="child.id">
                        <router-link :to="localePath(child.path)"
                            :class="{ 'active-link': isNavigationItemActive(child), 'nav-link--tagged': child.badgeKey }" @click="closeMobileMenu">
                            {{ $t(child.labelKey) }}<span v-if="child.badgeKey" class="nav-tag">{{ $t(child.badgeKey) }}</span>
                        </router-link>
                    </li>
                    <template v-if="activeNavigationGroup.id === 'tree'">
                        <li class="mobile-section-label">{{ $t('nav.ancestorLines') }}</li>
                        <li v-for="family in familyList" :key="family.name">
                            <button type="button" class="mobile-navigation-button" @click="openFamily(family.name)">
                                <span>{{ family.name }}</span><img src="@/assets/icons/dropdown.svg" alt="" />
                            </button>
                        </li>
                    </template>
                </ul>
            </div>
        </transition>

    </nav>
</template>

<script>
import authenticatedApiClient from '@/api/authenticatedApiClient';
import { debounce } from 'lodash';
import { useAuth } from '@/composables/useAuth';
import { SUPPORTED_LOCALES, useLocale } from '@/composables/useLocale';
import { formatYears } from '@/utils/dateUtils';
import { debugLog } from '@/utils/logger';
import { isPrivateLivingPerson } from '@/utils/personPrivacy';
import { SITE_NAVIGATION } from '@/config/siteNavigation';

import ThumannFamilyData from '@/data/getPatrilinealAncestors/ThumannFamily.json';
import MolletFamilyData from '@/data/getPatrilinealAncestors/MolletFamily.json';
import AkoloFamilyData from '@/data/getPatrilinealAncestors/AkoloFamily.json';
import JacobszFamilyData from '@/data/getPatrilinealAncestors/JacobszFamily.json';
import CrabbeFamilyData from '@/data/getPatrilinealAncestors/CrabbeFamily.json';
import CroixFamilyData from '@/data/getPatrilinealAncestors/CroixFamily.json';
import MullerFamilyData from '@/data/getPatrilinealAncestors/MullerFamily.json';
import NotenboomFamilyData from '@/data/getPatrilinealAncestors/NotenboomFamily.json';
import WesthoffFamilyData from '@/data/getPatrilinealAncestors/WesthoffFamily.json';
import AuliveldFamilyData from '@/data/getPatrilinealAncestors/AuliveldFamily.json';
import CordieFamilyData from '@/data/getPatrilinealAncestors/CordieFamily.json';

export default {
    name: 'NavBar',
    props: {
        isHome: {
            type: Boolean,
            default: false
        }
    },
    setup() {
        const { authToken, username, isAdmin, logout } = useAuth();
        const { locale, setLocale, localePath } = useLocale();

        return {
            authToken,
            username,
            isAdmin,
            logout,
            locale,
            setLocale,
            localePath,
        };
    },
    data() {
        return {
            dropdown: null,
            pinnedDropdown: null,
            dropdownRefs: {},
            lastDropdownTrigger: null,
            navigationItems: SITE_NAVIGATION,
            searchQuery: this.$route.query.q || '',
            searchSuggestions: [],  // To store the fetched suggestions
            searchResults: [],
            currentPage: 1,
            resultsPerPage: 10,
            totalResults: 0,
            isFocused: false,
            totalStories: 0,  // Initialize totalStories
            selectedFamily: null,  // For click-open state
            hoveredFamily: null,   // For hover-open state
            subdropdownOpen: false, // Track if subdropdown is open
            familySubdropdownRefs: {},
            subdropdownOffsets: {},
            isMenuOpen: false, // Track the state of the mobile menu
            activeSubmenu: null, // Tracks the currently open submenu
            activeFamily: null, // Tracks the active family for the family submenu
            familyList: [],
            ThumannFamily: ThumannFamilyData,
            MolletFamily: MolletFamilyData,
            AkoloFamily: AkoloFamilyData,
            JacobszFamily: JacobszFamilyData,
            CrabbeFamily: CrabbeFamilyData,
            CroixFamily: CroixFamilyData,
            MullerFamily: MullerFamilyData,
            NotenboomFamily: NotenboomFamilyData,
            WesthoffFamily: WesthoffFamilyData,
            AuliveldFamily: AuliveldFamilyData,
            CordieFamily: CordieFamilyData,
        };
    },
    created() {
        this.familyList = [
            { name: 'Thümann', slug: 'thumann', members: this.flattenAncestors(this.ThumannFamily) },
            { name: 'Mollet', slug: 'mollet', members: this.flattenAncestors(this.MolletFamily) },
            { name: 'Akolo', slug: 'akolo', members: this.flattenAncestors(this.AkoloFamily) },
            { name: 'Jacobsz', slug: 'jacobsz', members: this.flattenAncestors(this.JacobszFamily) },
            { name: 'Crabbe', slug: 'crabbe', members: this.flattenAncestors(this.CrabbeFamily) },
            { name: 'de la Croix', slug: 'de-la-croix', members: this.flattenAncestors(this.CroixFamily) },
            { name: 'Muller', slug: 'muller', members: this.flattenAncestors(this.MullerFamily) },
            { name: 'Notenboom', slug: 'notenboom', members: this.flattenAncestors(this.NotenboomFamily) },
            { name: 'Westhoff', slug: 'westhoff', members: this.flattenAncestors(this.WesthoffFamily) },
            { name: 'Auliveld', slug: 'auliveld', members: this.flattenAncestors(this.AuliveldFamily) },
            { name: 'Cordie', slug: 'cordie', members: this.flattenAncestors(this.CordieFamily) },
        ].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    },
    watch: {
        '$route.query.q': function (newQuery) {
            this.searchQuery = newQuery || '';
        },
        '$route.fullPath': function () {
            this.closeDropdown();
            this.closeMobileMenu();
        },
        isMenuOpen(isOpen) {
            document.body.classList.toggle('NavBar_body_menu_open', isOpen);
            if (isOpen) {
                this.$nextTick(() => this.$refs.mobileCloseButton?.focus());
            }
        },
    },
    computed: {
        isSearchPage() {
            // Check if the current route's name or path indicates the search page.
            // Adjust 'SearchResults' or '/search' as needed.
            return this.$route.name === 'SearchResults';
        },
        dropdownIconSrc() {
            return this.isHome
                ? require('@/assets/icons/dropdown_white.svg')
                : require('@/assets/icons/dropdown.svg');
        },
        isOverviewPage() {
            return ['Stamboom', 'PlaceOverview', 'PlaceEvents', 'AncestorTree'].includes(this.$route.name);
        },
        activeNavigationGroup() {
            return this.navigationItems.find(item => item.id === this.activeSubmenu)
                || { id: '', labelKey: 'nav.menu', children: [] };
        },
        currentSearchHref() {
            const trimmedQuery = (this.searchQuery || '').trim();
            return trimmedQuery
                ? `${this.localePath('/search')}?q=${encodeURIComponent(trimmedQuery)}&page=1`
                : this.localePath('/search');
        }
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside);
        document.addEventListener('click', this.handleClickOutsideSearchBox);
        window.addEventListener('resize', this.handleViewportResize);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleClickOutside);
        document.removeEventListener('click', this.handleClickOutsideSearchBox);
        window.removeEventListener('resize', this.handleViewportResize);
        document.body.classList.remove('NavBar_body_menu_open');
    },
    methods: {
        formatYears,
        isPrivateSuggestion(suggestion) {
            return isPrivateLivingPerson(suggestion, this.authToken);
        },
        localeTarget(lang) {
            const localePrefixPattern = new RegExp(`^/(?:${SUPPORTED_LOCALES.join('|')})(?=/|$)`);
            const currentPath = this.$route.path || '/';
            const unprefixedPath = currentPath.replace(localePrefixPattern, '') || '/';
            const path = lang === 'nl'
                ? unprefixedPath
                : `/${lang}${unprefixedPath === '/' ? '' : unprefixedPath}`;

            return { path, query: this.$route.query, hash: this.$route.hash };
        },
        localeHref(lang) {
            return this.$router.resolve(this.localeTarget(lang)).href;
        },
        changeLocale(lang) {
            this.setLocale(lang);
            this.closeMobileMenu();
            this.$router.replace(this.localeTarget(lang));
        },
        isNavigationItemActive(item) {
            const routeNames = item.routeNames || (item.routeName ? [item.routeName] : []);
            if (!routeNames.includes(this.$route.name)) return false;

            if (item.routeName === 'Stamboom' && item.view) {
                const routeView = ['achternamen', 'relaties'].includes(this.$route.query.view)
                    ? this.$route.query.view
                    : 'personen';
                return routeView === item.view;
            }

            return true;
        },
        isGroupActive(item) {
            return (item.children || []).some(child => this.isNavigationItemActive(child));
        },
        flattenAncestors(person) {
            const result = [];
            if (person) {
                result.push(person); // Add the current person
                if (person.ancestors && person.ancestors.length > 0) {
                    person.ancestors.forEach(ancestor => {
                        result.push(...this.flattenAncestors(ancestor));
                    });
                }
            }
            return result;
        },
        toggleFamily(familyName) {
            // Toggle off if already selected, otherwise set the selected family and open subdropdown
            if (this.selectedFamily === familyName) {
                this.selectedFamily = null;
                this.subdropdownOpen = false;
            } else {
                this.selectedFamily = familyName;
                this.subdropdownOpen = true;
                this.updateSubdropdownPosition(familyName);
            }
        },
        handleFamilyHover(familyName) {
            // When hovering over a family, update `hoveredFamily` and set `selectedFamily` to the hovered family.
            this.hoveredFamily = familyName;
            if (this.selectedFamily !== familyName) {
                // Clear `selectedFamily` if it's different from the hovered family.
                this.selectedFamily = null;
            }
            this.updateSubdropdownPosition(familyName);
        },
        isFamilyDropdownVisible(familyName) {
            // Display subdropdown if either hovered or selected
            return this.hoveredFamily === familyName || this.selectedFamily === familyName;
        },
        openDropdown(menu, event) {
            if (event?.currentTarget) this.lastDropdownTrigger = event.currentTarget;
            if (menu !== 'tree') {
                this.hoveredFamily = null;
                this.selectedFamily = null;
                this.subdropdownOpen = false;
            }
            this.dropdown = menu;
        },
        closeDropdown() {
            this.dropdown = null;
            this.pinnedDropdown = null;
            this.hoveredFamily = null;
            this.selectedFamily = null;
            this.subdropdownOpen = false;
        },
        toggleDropdown(menu, event) {
            this.lastDropdownTrigger = event?.currentTarget || this.lastDropdownTrigger;
            if (this.dropdown === menu && this.pinnedDropdown === menu) {
                this.closeDropdown();
            } else {
                this.pinnedDropdown = menu;
                this.openDropdown(menu);
            }
        },
        setDropdownRef(menu) {
            return element => {
                if (element) this.dropdownRefs[menu] = element;
                else delete this.dropdownRefs[menu];
            };
        },
        handleDesktopMenuLeave(event, menu) {
            this.$nextTick(() => {
                if (this.pinnedDropdown === menu) return;
                if (event.currentTarget?.contains(document.activeElement)) return;
                if (this.dropdown === menu) this.closeDropdown();
            });
        },
        handleDesktopFocusOut(event, menu) {
            if (event.currentTarget.contains(event.relatedTarget)) return;
            if (this.dropdown === menu) this.closeDropdown();
        },
        handleFamilyFocusOut(event, familyName) {
            if (event.currentTarget.contains(event.relatedTarget)) return;
            if (this.hoveredFamily === familyName) this.hoveredFamily = null;
        },
        handleEscape() {
            if (this.activeFamily || this.activeSubmenu) {
                this.closeSubmenu();
                return;
            }
            if (this.isMenuOpen) {
                this.closeMobileMenu();
                this.$nextTick(() => this.$refs.mobileMenuButton?.focus());
                return;
            }
            if (this.selectedFamily || this.hoveredFamily) {
                this.selectedFamily = null;
                this.hoveredFamily = null;
                this.subdropdownOpen = false;
                return;
            }
            if (this.dropdown) {
                const trigger = this.lastDropdownTrigger;
                this.closeDropdown();
                this.$nextTick(() => trigger?.focus());
            }
        },
        openSubmenu(submenu) {
            this.activeSubmenu = submenu;
            this.activeFamily = null;
            this.$nextTick(() => this.$refs.mobileBackButton?.focus());
        },
        closeSubmenu() {
            if (this.activeFamily) {
                this.activeFamily = null;
                this.$nextTick(() => this.$refs.mobileBackButton?.focus());
            } else {
                this.activeSubmenu = null;
                this.$nextTick(() => this.$refs.mobileCloseButton?.focus());
            }
        },
        openFamily(familyName) {
            this.activeFamily = familyName;
            this.$nextTick(() => this.$refs.mobileBackButton?.focus());
        },
        toggleMobileMenu() {
            if (this.isMenuOpen) this.closeMobileMenu();
            else this.isMenuOpen = true;
        },
        closeMobileMenu() {
            this.isMenuOpen = false;
            this.activeSubmenu = null;
            this.activeFamily = null;
        },
        handleViewportResize() {
            if (window.innerWidth > 840 && this.isMenuOpen) this.closeMobileMenu();
        },
        trapMobileFocus(event) {
            const panel = this.activeSubmenu
                ? this.$refs.mobileSubmenuPanel
                : this.$refs.mobileMenuPanel;
            if (!panel) return;

            const focusable = Array.from(panel.querySelectorAll(
                'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )).filter(element => !element.hasAttribute('inert'));
            if (!focusable.length) return;

            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        },
        setFamilySubdropdownRef(familyName) {
            return (element) => {
                if (element) {
                    this.familySubdropdownRefs[familyName] = element;
                } else {
                    delete this.familySubdropdownRefs[familyName];
                }
            };
        },
        getSubdropdownOffset(familyName) {
            return this.subdropdownOffsets[familyName] ?? -16;
        },
        updateSubdropdownPosition(familyName) {
            this.subdropdownOffsets = {
                ...this.subdropdownOffsets,
                [familyName]: -16,
            };

            this.$nextTick(() => {
                const subdropdown = this.familySubdropdownRefs[familyName];
                if (!subdropdown) return;

                const viewportPadding = 8;
                // Align the first person with the family row, accounting for menu padding.
                const baseOffset = -16;
                const rect = subdropdown.getBoundingClientRect();
                const bottomOverflow = rect.bottom - (window.innerHeight - viewportPadding);
                let offset = baseOffset;

                if (bottomOverflow > 0) {
                    offset -= bottomOverflow;
                }

                const adjustedTop = rect.top + (offset - baseOffset);
                if (adjustedTop < viewportPadding) {
                    offset += viewportPadding - adjustedTop;
                }

                this.subdropdownOffsets = {
                    ...this.subdropdownOffsets,
                    [familyName]: offset,
                };
            });
        },
        getFamilyMembers(familyName) {
            const family = this.familyList.find(f => f.name === familyName);
            return family ? family.members : [];
        },
        handleClickOutside(event) {
            if (this.$el && !this.$el.contains(event.target)) this.closeDropdown();
        },
        handleClickOutsideSearchBox(event) {
            const searchBoxRef = this.$refs.searchBox;
            if (searchBoxRef && !searchBoxRef.contains(event.target)) {
                // If the click is outside the search box, clear suggestions and lose focus
                this.isFocused = false;
                this.searchSuggestions = [];
            }
        },
        performSearch() {
            authenticatedApiClient
                .get('/search', {
                    params: {
                        q: this.searchQuery,
                        page: this.currentPage,
                        limit: this.resultsPerPage,
                    },
                })
                .then(response => {
                    this.searchResults = response.data.results;
                    this.totalResults = response.data.total;
                    debugLog(this.searchResults); // For debugging, show search results
                })
                .catch(error => {
                    console.error('Error during search:', error);
                });
        },
        onSearchInput: debounce(function () {
            if (this.searchQuery.length >= 2) {
                this.fetchSuggestions();
            } else {
                this.searchSuggestions = [];
            }
        }, 500),
        fetchSuggestions() {
            if (this.searchQuery.length < 2) {
                this.searchSuggestions = []; // Clear suggestions for short queries
                return;
            }

            authenticatedApiClient
                .get('/search/suggestions', {
                    params: { q: this.searchQuery },
                })
                .then(response => {
                    this.searchSuggestions = response.data.suggestions; // Populate suggestions array
                })
                .catch(error => {
                    console.error('Error fetching search suggestions:', error);
                });
        },
        selectSuggestion(suggestion) {
            this.searchSuggestions = [];
            this.isFocused = false;
            const targetPath = this.localePath(`/person/${suggestion.personID}`);

            if (this.$route?.path?.startsWith('/person/')) {
                window.location.assign(targetPath);
                return;
            }

            this.$router.push(targetPath);
        },
        openPath(path, event) {
            const shouldOpenInNewTab =
                event?.metaKey ||
                event?.ctrlKey ||
                event?.button === 1;

            if (shouldOpenInNewTab) {
                window.open(path, '_blank', 'noopener');
                return;
            }

            this.$router.push(path);
        },
        handleSuggestionClick(suggestion, event) {
            this.searchSuggestions = [];
            this.isFocused = false;
            const targetPath = this.isPrivateSuggestion(suggestion)
                ? this.localePath('/login')
                : this.localePath(`/person/${suggestion.personID}`);

            const shouldOpenInNewTab =
                event?.metaKey ||
                event?.ctrlKey ||
                event?.button === 1;

            if (shouldOpenInNewTab) {
                window.open(targetPath, '_blank', 'noopener');
                return;
            }

            window.location.assign(targetPath);
        },
        handleSuggestionAuxClick(suggestion, event) {
            if (event.button === 1) {
                this.handleSuggestionClick(suggestion, event);
            }
        },
        submitSearch() {
            this.searchSuggestions = [];  // Clear the suggestions when submitting the search
            this.currentPage = 1;
            this.isFocused = false;
            const trimmedQuery = (this.searchQuery || '').trim();
            const query = trimmedQuery ? { q: trimmedQuery, page: 1 } : {};
            this.$router.push({ path: this.localePath('/search'), query });
        },
        nextPage() {
            if (this.currentPage < Math.ceil(this.totalResults / this.resultsPerPage)) {
                this.currentPage++;
                this.performSearch();
            }
        },
        prevPage() {
            if (this.currentPage > 1) {
                this.currentPage--;
                this.performSearch();
            }
        },
    }
}
</script>

<style scoped>
.nav-overlay {
    position: fixed !important;
    inset: 0;
    background: rgba(0, 0, 0, 0.24);
    z-index: 1010 !important;
    pointer-events: none;
}

.nav-overlay-fade-enter-active,
.nav-overlay-fade-leave-active {
    transition: opacity 0.2s ease;
}

.nav-overlay-fade-enter-from,
.nav-overlay-fade-leave-to {
    opacity: 0;
}

.NavBar {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 24px 48px;
    position: relative;
    z-index: auto !important;
}

.NavBar__dropdown-trigger--active {
    position: relative;
    z-index: 1020;
}

.NavBar__search--active {
    position: relative;
    z-index: 1020;
}

.NavBar--overview {
    background-color: white;
    /* border-bottom: 1px solid var(--brand-bg-2); */
    box-shadow: var(--shadow-card-sm);
    /* color: var(--brand-text-dark); */
}

.NavBar--overview .search-box {
    border: 2px solid var(--brand-bg-2);
}

.NavBar--home a:not(.dropdown a, .mobile-menu a, .mobile-submenu a, .all-search-results a),
.NavBar--home span:not(.dropdown span, .search-suggestions span) {
    color: var(--brand-text-light) !important;
}

ul {
    display: flex;
    align-items: center;
    gap: 24px;
}

.height-fix {
    min-height: 62px;
}

a {
    position: relative;
}

a,
.a,
.search-suggestions span:nth-of-type(1) {
    font-weight: 600;
}

a:hover,
.a:hover {
    text-decoration: underline;
}

.a {
    padding: 6px 12px;
    border-radius: 4px;
    border: 2px solid rgba(255, 255, 255, 0);
    margin: 0 -14px;
    transition: all .15s ease;
}

.nav-disclosure,
.dropdown-action,
.mobile-navigation-button,
.mobile-icon-button {
    border: 0;
    background: transparent;
    color: inherit;
    cursor: pointer;
    font: inherit;
}

.nav-disclosure {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 600;
}

.nav-disclosure:hover,
.nav-disclosure:focus-visible,
.mobile-navigation-button:hover,
.mobile-navigation-button:focus-visible,
.dropdown-action:hover,
.dropdown-action:focus-visible {
    text-decoration: underline;
}

.nav-tag {
    display: inline-flex;
    align-items: center;
    margin-left: 8px;
    padding: 0 8px;
    border-radius: 999px;
    background: var(--brand-color-1);
    color: var(--brand-text-light);
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

hr {
    width: 100%;
    height: 1px;
    border: none;
    background-color: var(--brand-bg-2);
    margin: 8px 0;
}

.hr {
    width: calc(100% - 48px);
    border-top: 1px solid var(--brand-bg-2);
    margin: 14px auto;
}

.dropdown {
    position: absolute;
    background-color: var(--brand-bg-1);
    box-shadow: var(--shadow-card-lg);
    list-style: none;
    padding: 16px 0;
    display: flex;
    gap: 0;
    flex-direction: column;
    align-items: flex-start;
    /* top: calc(67px + 24px); */
    margin-left: -24px;
    /* margin-top: 8px; */
    border-radius: 16px;
    width: 242px;
    z-index: 1030;
}

.dropdown--language {
    right: 12px;
    left: auto;
    margin-left: 0;
    min-width: 220px;
    max-width: min(280px, calc(100vw - 24px));
}

.language-disclaimer {
    font-size: 0.75rem;
    color: #888;
    font-style: italic;
    padding: 6px 12px;
    border-top: 1px solid #eee;
    cursor: default;
}

.dropdown li:not(.subdropdown li) {
    /* padding: 6px 24px; */
    width: 100%;
}

.dropdown li:not(.subdropdown li) a,
.subdropdown-link {
    padding: 6px 24px;
    font-weight: 600;
}

.dropdown-action {
    width: 100%;
    padding: 6px 24px;
    text-align: left;
    font-weight: 600;
}

.dropdown-divider {
    height: 1px;
    margin: 8px 24px;
    background: var(--brand-bg-2);
    cursor: default !important;
    width: calc(100% - 48px) !important;
}

.dropdown-section-label,
.mobile-section-label {
    color: #767676;
    cursor: default !important;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.dropdown-section-label {
    padding: 4px 24px;
}

.dropdown--lg {
    padding: 48px;
    gap: 48px;
    flex-direction: column;
    margin-left: 0;
    left: 12px;
    max-width: calc(100vw - 24px);
}

.dropdown--lg a,
.dropdown--lg .a {
    align-items: flex-start;
    flex-direction: column;
    gap: 0;
}

/* .dropdown--lg a span:nth-of-type(1),
.dropdown--lg .a span:nth-of-type(1) {
    color: var(--brand-color-1);
} */

.dropdown--lg a span:nth-of-type(2),
.dropdown--lg .a span:nth-of-type(2),
.search-suggestions span:nth-of-type(2) {
    font-size: 12px;
    font-weight: 400;
    opacity: .5;
}

/* .active-dropdown {
    text-decoration: underline;
    border: 2px solid var(--brand-text-dark);
    transition: all .15s ease;
}

.active-dropdown-home {
    text-decoration: underline;
    border: 2px solid rgba(255, 255, 255, 1);
    transition: all .15s ease;
} */

.dropdown_col {
    display: flex;
    gap: 12px;
    flex-direction: column;
}

.dropdown_row {
    display: flex;
    flex-direction: row;
    gap: 48px;
    /* justify-content: space-between; */
    flex-wrap: wrap;
    width: 100%;
}

.NavBar ul li:hover .dropdown {
    display: flex;
}

/* Dropdown transition */
.dropdown-transition {
    /* overflow: hidden; */
    height: 0;
    padding-top: 0;
    padding-bottom: 0;
    transition: var(--transition-ease);
}

.dropdown-transition-enter-active,
.dropdown-transition-leave-active {
    transition: var(--transition-ease);
}

.dropdown-transition-enter,
.dropdown-transition-leave-to {
    height: 0;
    padding-top: 0;
    padding-bottom: 0;
}

.dropdown-transition-enter-to {
    height: auto;
    padding-top: 24px;
    padding-bottom: 24px;
}

.dropdown--lg .dropdown-transition-enter-to {
    padding-top: 48px;
    padding-bottom: 48px;
}

.dropdown li {
    font-weight: 600;
    cursor: pointer;
    width: 100%;
}

.dropdown li:hover {
    background-color: #f0f0f0;
}

li:not(.search-li, .search-suggestions li):hover {
    text-decoration: underline;
}

.all-search-results {
    font-weight: 600;
    color: var(--brand-color-1);
}

.search-suggestions .all-search-results a {
    color: var(--brand-color-1);
}

/* .story_count {
    position: absolute;
    font-size: 8px;
    font-weight: 700;
    background: var(--brand-color-1);
    color: var(--brand-text-light);
    border-radius: 999px;
    display: flex;
    height: 16px;
    width: 16px;
    align-items: center;
    justify-content: center;
    top: -10px;
    right: -16px;
} */

.story_count {
    font-size: 12px;
    font-weight: 700;
    background: var(--brand-color-1);
    color: var(--brand-text-light);
    border-radius: 4px;
    display: flex;
    height: 20px;
    width: 24px;
    align-items: center;
    justify-content: center;
}

.story_count--home {
    background: var(--brand-color-3);
    color: var(--brand-text-dark);
}

.story_count:hover {
    text-decoration: none;
}

.active-link {
    text-decoration: underline;
    color: var(--brand-color-1);
}

.logged_in {
    display: flex;
    flex-direction: row;
    gap: 24px;
}

.subdropdown-link {
    display: flex;
    width: 100%;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    border: 0;
    background: transparent;
    color: inherit;
    cursor: pointer;
    font: inherit;
    font-weight: 600;
    text-align: left;
}

.subdropdown-link img {
    opacity: 0.2;
    transition: transform 0.2s cubic-bezier(0.49, 1.42, 0.25, 1.53), opacity 0.2s ease;
}

.subdropdown-link:hover img {
    opacity: 1;
    transform: rotate(-90deg);
}

.ancestor-family {
    position: relative;
}

/* Style for the family subdropdown */
.subdropdown {
    /* position: absolute;
    top: 100%;
    left: 0;
    background-color: var(--brand-bg-1);
    box-shadow: var(--shadow-card-lg);
    padding: 16px;
    border-radius: var(--border-radius-md);
    display: flex;
    flex-direction: column;
    gap: 12px;
    z-index: 10; */

    display: flex;
    gap: 0;
    flex-direction: column;
    align-items: flex-start;
    background-color: var(--brand-bg-1);
    position: absolute;
    top: 0;
    padding: 16px 0;
    /* margin-top: -24px; */
    border-radius: 16px;
    box-shadow: var(--shadow-card-lg);
    width: max-content;
    left: calc(100% - 8px);
    max-height: calc(100vh - 16px);
    overflow-y: auto;
    z-index: 1040;
    transform: translateY(var(--subdropdown-offset-y, -16px));
}

.subdropdown a {
    align-items: flex-start;
    flex-direction: column;
    padding: 6px 24px;
    gap: 0;
}

.subdropdown span:nth-of-type(2) {
    font-size: 12px;
    font-weight: 400;
    opacity: .5;
}

.locked_name:hover {
    text-decoration: underline;
}

.show-on-mobile,
.show-on-tablet {
    display: none;
}

.show-on-mobile.button-group a,
.show-on-tablet a,
.mobile-icon-button,
.close-button,
.back-button {
    display: flex;
    width: 44px;
    height: 44px;
    justify-content: center;
    align-items: center;
    gap: 8px;
    border-radius: 100%;
    background: var(--brand-color-1);
    transition: var(--transition-bounce);
    box-shadow: 0px 0px 0px 0px var(--brand-color-1);
    border: none;
    cursor: pointer;
}

.mobile-menu {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100dvh;
    overflow-y: auto;
    background-color: white;
    /* Adjust as needed */
    z-index: 1000;
    padding: 24px 20px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-end;
    gap: 24px
}

.mobile-menu-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
}

.mobile-menu ul li,
.mobile-submenu ul li {
    width: 100%;
}

.mobile-menu ul a,
.mobile-submenu ul a,
.mobile-navigation-button {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
}

.mobile-navigation-button {
    min-height: 44px;
    padding: 0;
    color: var(--brand-text-dark);
    font-weight: 600;
    text-align: left;
}

.mobile-submenu .nav-link--tagged {
    justify-content: flex-start;
}

.mobile-navigation-button img {
    width: 16px;
    height: 16px;
}

.mobile-menu-header a img {
    height: 40px;
}

/* Enter transition */
.menu-slide-enter-active {
    transition: all 0.2s ease-out;
}

.menu-slide-enter-from,
.menu-slide-leave-to {
    transform: translateX(100%);
}

.menu-slide-enter-to,
.menu-slide-leave-from {
    transform: translateX(0);
}

/* Leave transition */
.menu-slide-leave-active {
    transition: var(--transition-bounce);
}

.submenu-slide-enter-active {
    transition: all 0.2s ease-out;
}

.submenu-slide-leave-active {
    transition: var(--transition-bounce);
}

.submenu-slide-enter-from {
    transform: translateX(100%);
}

.submenu-slide-enter-to,
.submenu-slide-leave-from {
    transform: translateX(0);
}

.submenu-slide-leave-to {
    transform: translateX(100%);
}

.mobile-menu .dropdown {
    position: static;
    box-shadow: none;
    padding: 0;
    width: 100%;
}

/* .mobile-menu .dropdown li a {
    padding: 0 !important;
    font-size: 16px;
    font-weight: 400;
    text-decoration: none !important;
} */

.mobile-menu ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
    width: 100%;
    gap: 16px;
}

.mobile-menu ul li a,
.mobile-submenu ul li a {
    min-height: 44px;
    text-decoration: none;
    /* font-size: 1.5rem; */
    /* font-weight: bold; */
    color: var(--brand-text-dark);
}

.mobile-language-links {
    display: flex;
    align-items: center;
    gap: 8px;
}

.mobile-language-links a {
    width: auto !important;
}

.mobile-section-label {
    margin-top: 8px;
    padding-top: 16px;
    border-top: 1px solid var(--brand-bg-2);
}

:global(body.NavBar_body_menu_open) {
    overflow: hidden;
}

.mobile-menu ul li a:hover {
    text-decoration: underline;
}

.mobile-submenu {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    max-height: 100vh;
    height: 100%;
    /* Limit the submenu height to the viewport */
    overflow-y: auto;
    /* Enable vertical scrolling when necessary */
    background-color: white;
    z-index: 1100;
    padding: 24px 20px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 16px;
}

.mobile-menu-header {
    flex-direction: column;
    gap: 24px;
}

.mobile-submenu ul {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    gap: 16px;
}

.mobile-submenu .person {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
}

.mobile-submenu .person span:nth-of-type(2) {
    font-size: 12px;
    font-weight: 400;
    opacity: .5;
}

.search-box input {
    font-weight: 400;
    cursor: text;
}

.search-box:hover,
.search-box input:hover {
    text-decoration: none;
}

@media (max-width: 1320px) {
    .NavBar {
        padding: 24px 20px;
    }

    .hide-on-tablet {
        display: none;
    }

    .show-on-tablet {
        display: inline-block;
    }
}

@media (max-width: 840px) {
    .hide-on-mobile {
        display: none;
    }

    .show-on-mobile {
        display: flex;
        flex-direction: row;
        gap: 8px;
    }
}

@media (min-width: 841px) {
    .mobile-menu,
    .mobile-submenu {
        display: none;
    }
}
</style>
